import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SessionView } from '@mdt/contracts';
import { clearQueryCache, command, draftKey, fetchSession, keepAlive, logoutRequest, apiEvents, query } from './api.ts';
import { openSessionChannel, type SessionMessage } from './broadcast.ts';
import { DraftKeyring } from './drafts/keyring.ts';
import { closeDb } from './drafts/store.ts';
import { currentLocale, setLocale, type UiLocale } from './i18n.ts';
import { IdleController, type IdlePhase } from './idle.ts';
import { useRouter } from './router.tsx';
import { unsubscribeThisBrowser, wipeLocalData } from './wipe.ts';

/**
 * The signed-in session on the client (roadmap 3.3, 3.4). The server is the authority: this provider
 * polls it (without X-MDT-Activity, so polling never keeps a session alive), runs the idle timer that
 * mirrors the server's deadline, and tears everything down when the session ends:
 *
 * - explicit sign-out (both modes) and shared-mode expiry: cancel this browser's push subscription, revoke
 *   on the server (which destroys the draft key), wipe local data, tell the other tabs, show /signed-out;
 * - standard-mode idle expiry: back to the sign-in screen, drafts kept (the device key survives idle
 *   expiry on the server too, so they open again after signing back in).
 */
export type Calendar = 'be' | 'ce';
const CALENDAR_STORAGE_KEY = 'mdt.calendar';

type SignedIn = { status: 'signedIn'; view: SessionView; keyring: DraftKeyring };
export type SessionState = { status: 'loading' } | { status: 'signedOut'; messageKey?: string } | SignedIn;

type SessionApi = {
  state: SessionState;
  refresh(): Promise<void>;
  signOut(): Promise<void>;
  idle: { phase: IdlePhase; deadline: number; stay(): Promise<boolean> };
  calendar: Calendar;
  setCalendar(c: Calendar): void;
  setLanguage(l: UiLocale): void;
};

const SessionContext = createContext<SessionApi | null>(null);

const POLL_MS = 60_000;

function readCalendar(): Calendar {
  try {
    return localStorage.getItem(CALENDAR_STORAGE_KEY) === 'ce' ? 'ce' : 'be';
  } catch {
    return 'be';
  }
}

function writeCalendar(c: Calendar) {
  try {
    localStorage.setItem(CALENDAR_STORAGE_KEY, c);
  } catch {
    // storage disabled
  }
}

/** Applies the saved language and calendar; if none were ever saved, saves this browser's choice once. */
async function syncPrefs(view: SessionView, storeCalendar: (c: Calendar) => void) {
  if (view.ghost) return;
  const prefs = await query<{ locale: UiLocale | null; calendar: Calendar | null }>('prefs.mine').catch(() => null);
  if (!prefs) return;
  if (prefs.locale) setLocale(prefs.locale);
  if (prefs.calendar) storeCalendar(prefs.calendar);
  if (prefs.locale === null) {
    // push texts are rendered in the saved language, so save what the person chose before signing in
    void command('prefs.update', { locale: currentLocale(), calendar: readCalendar() }, { activity: false }).catch(() => {});
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { navigate } = useRouter();
  const queryClient = useQueryClient();
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const [idlePhase, setIdlePhase] = useState<IdlePhase>('active');
  const [idleDeadline, setIdleDeadline] = useState(0);
  const [calendar, setCalendarState] = useState<Calendar>(readCalendar);
  const stateRef = useRef(state);
  stateRef.current = state;
  const idleRef = useRef<IdleController | null>(null);
  /** The newest idle deadline the server reported (ISO). A poll moves the local timer only past this. */
  const serverIdleRef = useRef('');
  const channel = useMemo(() => openSessionChannel(), []);

  const storeCalendar = useCallback((c: Calendar) => {
    setCalendarState(c);
    writeCalendar(c);
  }, []);

  /** Ends the session on this browser. `wipe` = this tab does the cleanup (others only close handles). */
  const teardown = useCallback(async (o: { serverLogout: boolean; wipe: boolean; reason: 'logout' | 'idle' | 'expired'; mode: 'standard' | 'shared' }) => {
    idleRef.current?.stop();
    idleRef.current = null;
    const s = stateRef.current;
    if (s.status === 'signedIn') s.keyring.forget();
    if (o.wipe) await unsubscribeThisBrowser();
    if (o.serverLogout) await logoutRequest();
    if (o.wipe) await wipeLocalData();
    else await closeDb();
    clearQueryCache();
    queryClient.clear();
    setIdlePhase('active');
    setState({ status: 'signedOut' });
    navigate(`/signed-out?reason=${o.reason}&mode=${o.mode}`, { replace: true });
  }, [navigate, queryClient]);

  const signOutAs = useCallback(async (reason: 'logout' | 'idle' | 'expired') => {
    const s = stateRef.current;
    const mode = s.status === 'signedIn' ? s.view.mode : 'standard';
    channel.post({ type: 'logout', reason, mode });
    await teardown({ serverLogout: true, wipe: true, reason, mode });
  }, [channel, teardown]);

  /** The server says the session is gone (revoked, expired, disabled) while we thought it was live. */
  const lost = useCallback(async (messageKey: string) => {
    const s = stateRef.current;
    if (s.status !== 'signedIn') return;
    if (s.view.mode === 'shared') {
      await signOutAs('expired');
      return;
    }
    idleRef.current?.stop();
    idleRef.current = null;
    s.keyring.forget();
    clearQueryCache();
    queryClient.clear();
    setState({ status: 'signedOut', messageKey });
  }, [queryClient, signOutAs]);

  const load = useCallback(async (activity: boolean) => {
    let res;
    try {
      res = await fetchSession(activity);
    } catch {
      if (stateRef.current.status === 'loading') setState({ status: 'signedOut', messageKey: 'err.network' });
      return;
    }
    if (!res.signedIn) {
      if (stateRef.current.status === 'signedIn') await lost(res.messageKey);
      // no cookie at all is simply "signed out"; an ended session says why (idle, revoked, …)
      else setState(res.messageKey === 'err.unauthenticated' ? { status: 'signedOut' } : { status: 'signedOut', messageKey: res.messageKey });
      return;
    }
    const view = res.view;
    const prev = stateRef.current;
    const localDeadline = Date.now() + (Date.parse(view.idleExpiresAt) - Date.parse(view.serverTime));
    // entering or leaving a ghost session (or its expiry) changes whose data this app shows: nothing cached may carry over
    if (prev.status === 'signedIn' && prev.view.ghost?.ghostSessionId !== view.ghost?.ghostSessionId) {
      clearQueryCache();
      queryClient.clear();
    }
    if (prev.status === 'signedIn' && prev.view.person.personId === view.person.personId && prev.view.mode === view.mode
      && prev.view.ghost?.ghostSessionId === view.ghost?.ghostSessionId) {
      setState({ ...prev, view });
      // Only evidence of new activity on the server (another tab, a command) moves the local deadline;
      // re-deriving it from every poll could only ever make it later than the server's.
      if (view.idleExpiresAt > serverIdleRef.current) {
        serverIdleRef.current = view.idleExpiresAt;
        idleRef.current?.adopt(localDeadline);
      }
      return;
    }
    serverIdleRef.current = view.idleExpiresAt;
    const keyring = new DraftKeyring(view.person.personId, view.mode, draftKey);
    setState({ status: 'signedIn', view, keyring });
    if (prev.status !== 'signedIn') channel.post({ type: 'signed-in' });
    void keyring.prepare().catch(() => {});
    // A lab computer keeps nobody's push subscription (spec §1), not even one a previous user left.
    if (view.mode === 'shared') void unsubscribeThisBrowser();
    void syncPrefs(view, storeCalendar);

    idleRef.current?.stop();
    const idle = new IdleController({
      timeoutMs: view.idleTimeoutSeconds * 1000,
      initialDeadline: localDeadline,
      recordEveryMs: view.mode === 'shared' ? 30_000 : 60_000,
      keepAlive: async () => {
        const r = await keepAlive();
        if (r.idleExpiresAt > serverIdleRef.current) serverIdleRef.current = r.idleExpiresAt;
      },
      channel,
      onPhase: (phase, deadline) => {
        setIdlePhase(phase);
        setIdleDeadline(deadline);
        if (phase !== 'expired') return;
        if (view.mode === 'shared') void signOutAs('idle');
        else void lost('err.session.idle_expired');
      },
    });
    idleRef.current = idle;
    setIdleDeadline(localDeadline);
    setIdlePhase('active');
    idle.start();
  }, [channel, lost, signOutAs, storeCalendar, queryClient]);

  // First load (opening the app is the person's own action), then background polls.
  useEffect(() => {
    void load(true);
    const poll = setInterval(() => void load(false), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      idleRef.current?.check();
      void load(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // Activity events feed the idle timer (throttled inside it).
  useEffect(() => {
    const onActivity = () => idleRef.current?.activity();
    const onVisible = () => {
      if (document.visibilityState === 'visible') onActivity();
    };
    const opts = { passive: true, capture: true } as const;
    for (const ev of ['pointerdown', 'keydown', 'touchstart'] as const) globalThis.addEventListener(ev, onActivity, opts);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      for (const ev of ['pointerdown', 'keydown', 'touchstart'] as const) globalThis.removeEventListener(ev, onActivity, opts);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // The API saw a 401 somewhere (a command, a query): the session ended under us.
  useEffect(() => {
    const onUnauth = (e: Event) => void lost((e as CustomEvent<string>).detail);
    apiEvents.addEventListener('unauthenticated', onUnauth);
    return () => apiEvents.removeEventListener('unauthenticated', onUnauth);
  }, [lost]);

  // Other tabs: a sign-out anywhere ends this tab too; a sign-in elsewhere refreshes a signed-out tab.
  useEffect(() => channel.subscribe((m: SessionMessage) => {
    if (m.type === 'logout' && stateRef.current.status === 'signedIn') {
      void teardown({ serverLogout: false, wipe: false, reason: m.reason, mode: m.mode });
    } else if (m.type === 'signed-in' && stateRef.current.status !== 'signedIn') {
      void load(false);
    }
  }), [channel, teardown, load]);

  const setLanguage = useCallback((l: UiLocale) => {
    setLocale(l);
    if (stateRef.current.status === 'signedIn') void command('prefs.update', { locale: l }).catch(() => {});
  }, []);

  const setCalendar = useCallback((c: Calendar) => {
    storeCalendar(c);
    if (stateRef.current.status === 'signedIn') void command('prefs.update', { calendar: c }).catch(() => {});
  }, [storeCalendar]);

  const value = useMemo<SessionApi>(() => ({
    state,
    refresh: () => load(false),
    signOut: () => signOutAs('logout'),
    idle: { phase: idlePhase, deadline: idleDeadline, stay: () => idleRef.current?.stay() ?? Promise.resolve(false) },
    calendar,
    setCalendar,
    setLanguage,
  }), [state, load, signOutAs, idlePhase, idleDeadline, calendar, setCalendar, setLanguage]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** The signed-in view; screens behind the sign-in guard only. */
export function useSignedIn(): SignedIn {
  const { state } = useSession();
  if (state.status !== 'signedIn') throw new Error('useSignedIn while signed out');
  return state;
}
